// Catalog of cockpit widgets. Each entry defines metadata only:
// id, display title, and a default RGL layout slot. The actual JSX is
// provided by the page through `widgetProps[id]` so existing
// section markup + memoized rows + data-hook wiring stay on the page
// (CoinTerminalPage). Adding a new widget = one entry here + one entry
// in the page's widgetProps map.
//
// 12-column RGL grid, rowHeight 30 → desktop reference ~1200×900.

// Each chart variant ships its own RGL cell so users can stack a "whales
// only" view next to a "liquidations only" view, sized independently.
// The `category` field drives grouping in the side panel (Library).
// `multi: true` widgets can be added more than once per tab — each click on
// Library spawns a fresh instance with a unique id `${type}#${rand}`. Single
// widgets (no `multi` flag) work as toggles like before. `instanceTypeOf(id)`
// strips the `#xxx` suffix so the registry can still be type-keyed.
export const WIDGET_REGISTRY = {
  chart: {
    id: 'chart',
    title: 'Chart (All)',
    category: 'Charts',
    description: 'Price candles with user-configurable indicators.',
    defaultLayout: { x: 0, y: 0, w: 9, h: 22, minW: 4, minH: 10 },
    multi: true,
  },
  'chart-whales': {
    id: 'chart-whales',
    title: 'Chart · Whales',
    category: 'Charts',
    description: 'Price + whale trade bubbles only.',
    defaultLayout: { x: 0, y: 22, w: 6, h: 14, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-liquidations': {
    id: 'chart-liquidations',
    title: 'Chart · Liquidations',
    category: 'Charts',
    description: 'Price + long/short liquidation bubbles only.',
    defaultLayout: { x: 6, y: 22, w: 6, h: 14, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-orders': {
    id: 'chart-orders',
    title: 'Chart · Order Whales',
    category: 'Charts',
    description: 'Price + large limit-order price lines only.',
    defaultLayout: { x: 0, y: 36, w: 12, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-sma-20': {
    id: 'chart-sma-20',
    title: 'Chart · SMA 20',
    category: 'Charts',
    description: 'Price candles with the 20-period simple moving average.',
    defaultLayout: { x: 0, y: 48, w: 6, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-sma-50': {
    id: 'chart-sma-50',
    title: 'Chart · SMA 50',
    category: 'Charts',
    description: 'Price candles with the 50-period simple moving average.',
    defaultLayout: { x: 6, y: 48, w: 6, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-ema-20': {
    id: 'chart-ema-20',
    title: 'Chart · EMA 20',
    category: 'Charts',
    description: 'Price candles with the 20-period exponential moving average.',
    defaultLayout: { x: 0, y: 60, w: 6, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-ema-50': {
    id: 'chart-ema-50',
    title: 'Chart · EMA 50',
    category: 'Charts',
    description: 'Price candles with the 50-period exponential moving average.',
    defaultLayout: { x: 6, y: 60, w: 6, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'chart-ichimoku': {
    id: 'chart-ichimoku',
    title: 'Chart · Ichimoku Cloud',
    category: 'Charts',
    description: 'Price candles with the full Ichimoku Cloud (Tenkan, Kijun, Senkou A/B).',
    defaultLayout: { x: 0, y: 72, w: 12, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'liqmap-hyperliquid': {
    id: 'liqmap-hyperliquid',
    title: 'Liq Map · Hyperliquid',
    category: 'Liquidation Maps',
    description: 'Real Hyperliquid position-based liquidation buckets.',
    defaultLayout: { x: 0, y: 60, w: 4, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'liqmap-synthetic': {
    id: 'liqmap-synthetic',
    title: 'Liq Map · Synthetic',
    category: 'Liquidation Maps',
    description: 'Coinglass-style model from open-interest snapshots.',
    defaultLayout: { x: 4, y: 60, w: 4, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'liqmap-combined': {
    id: 'liqmap-combined',
    title: 'Liq Map · Combined',
    category: 'Liquidation Maps',
    description: 'Per-bucket sum across HL + dYdX + synthetic.',
    defaultLayout: { x: 8, y: 60, w: 4, h: 12, minW: 3, minH: 8 },
    multi: true,
  },
  'position-summary': {
    id: 'position-summary',
    title: 'Position Summary',
    category: 'Data',
    description: '24h whale position rollup for this coin.',
    defaultLayout: { x: 9, y: 0, w: 3, h: 4, minW: 2, minH: 3 },
    pinned: true,
  },
  whales: {
    id: 'whales',
    title: 'Whale Trades',
    category: 'Lists',
    description: 'Live list of large trades crossing the tape.',
    defaultLayout: { x: 9, y: 4, w: 3, h: 9, minW: 2, minH: 4 },
    multi: true,
  },
  liquidations: {
    id: 'liquidations',
    title: 'Liquidations',
    category: 'Lists',
    description: 'Live long/short liquidations feed.',
    defaultLayout: { x: 9, y: 13, w: 3, h: 9, minW: 2, minH: 4 },
    multi: true,
  },
  orders: {
    id: 'orders',
    title: 'Order Whales',
    category: 'Lists',
    description: 'Resting large limit orders aggregated by price level.',
    defaultLayout: { x: 0, y: 48, w: 6, h: 6, minW: 2, minH: 4 },
    multi: true,
  },
  news: {
    id: 'news',
    title: 'News',
    category: 'Lists',
    description: 'Real-time crypto news feed.',
    defaultLayout: { x: 6, y: 48, w: 6, h: 6, minW: 2, minH: 4 },
    multi: true,
  },
  'liq-volume': {
    id: 'liq-volume',
    title: 'Liquidation Volume',
    category: 'Derivatives',
    description: 'Per-candle liquidation USD volume — long/short split.',
    defaultLayout: { x: 0, y: 72, w: 6, h: 8, minW: 3, minH: 5 },
    multi: true,
  },
  'long-short': {
    id: 'long-short',
    title: 'Long/Short Ratio',
    category: 'Derivatives',
    description: 'Binance Futures long/short account ratio over time.',
    defaultLayout: { x: 6, y: 72, w: 6, h: 8, minW: 3, minH: 5 },
    multi: true,
  },
  'trade-volume': {
    id: 'trade-volume',
    title: 'Trade Volume',
    category: 'Derivatives',
    description: 'Taker buy/sell volume per candle with optional CVD.',
    defaultLayout: { x: 0, y: 80, w: 6, h: 8, minW: 3, minH: 5 },
    multi: true,
  },
};

// Every widget id, used by the Library panel to show *all* available
// widgets — including hidden-by-default chart variants — with toggles.
export const ALL_WIDGET_IDS = [
  'chart',
  'chart-whales',
  'chart-liquidations',
  'chart-orders',
  'chart-sma-20',
  'chart-sma-50',
  'chart-ema-20',
  'chart-ema-50',
  'chart-ichimoku',
  'liqmap-hyperliquid',
  'liqmap-synthetic',
  'liqmap-combined',
  'position-summary',
  'whales',
  'liquidations',
  'orders',
  'news',
  'liq-volume',
  'long-short',
  'trade-volume',
];

// Order matters: drives Library grouping and `buildDefaultLayout()` output.
// Initial defaults: 1 main chart + the 4 data widgets visible.
// Specialised chart variants exist in the registry but start hidden so the
// out-of-the-box layout isn't a 4×chart eyesore.
//
// This is the *factory* fallback shown only when (a) the user has no
// localStorage and (b) the admin hasn't published a server-side default
// for this scope via /api/admin/cockpit/default-tabs. The curated layout
// (e.g. preset eGkKq2m) lives on the server so admins can update it
// without a redeploy — see server/routes/cockpitDefaults.js.
export const DEFAULT_WIDGET_IDS = [
  'chart',
  'position-summary',
  'whales',
  'liquidations',
  'orders',
  'news',
];

export function buildDefaultLayout() {
  return DEFAULT_WIDGET_IDS.map((id) => ({
    i: id,
    ...WIDGET_REGISTRY[id].defaultLayout,
  }));
}

// Multi-instance widgets carry a `#` suffix on their layout id; the part
// before the `#` is the registry type. Singletons (`position-summary`)
// use the bare type as id.
export function instanceTypeOf(id) {
  if (!id) return id;
  const idx = id.indexOf('#');
  return idx === -1 ? id : id.slice(0, idx);
}

// Short, URL-safe instance suffix — collisions inside a single tab are
// vanishingly unlikely with timestamp + random base36.
export function makeInstanceId(type) {
  return `${type}#${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
}
